// Wydruk listy rejestru — odpowiednik „Drukuj listę" z Magistratu
// (Documents.Register.UI: DevExpress PrintableControlLink, A4 poziomo, nagłówek z
// nazwą rejestru i okresem). Ten sam mechanizm co karty nadawcze (OutgoingView.doPrint):
// nowe okno z HTML + window.print(), bez PDF po stronie BE.

export interface IPrintRegisterColumn<T> {
  header: string;
  /** Wartość komórki; null/undefined → pusta. */
  value: (row: T, index: number) => string | number | null | undefined;
  /** Szerokość kolumny (CSS), np. "12%" / "28mm". */
  width?: string;
  align?: "left" | "center" | "right";
}

export interface IPrintRegisterOptions<T> {
  title: string;
  /** Podtytuł — np. „Okres: 01.09.2026 – 16.09.2026". */
  subtitle?: string | null;
  columns: IPrintRegisterColumn<T>[];
  rows: T[];
  /** Wiersze podsumowania pod tabelą (np. „Suma opłat: 123,45 zł"). */
  footer?: string[];
  /** Dodatkowa kolumna „Podpis" (lista doręczeń — PrintEmployeesDeliveryConfirmation). */
  signatureColumn?: boolean;
  landscape?: boolean;
}

const esc = (v: unknown) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

/**
 * Otwiera okno wydruku z tabelą rejestru. Zwraca false, gdy przeglądarka zablokowała
 * wyskakujące okno (wołający pokazuje powiadomienie).
 */
export const printRegister = <T>(opts: IPrintRegisterOptions<T>): boolean => {
  const landscape = opts.landscape ?? true;
  const columns = [...opts.columns];
  if (opts.signatureColumn) {
    columns.push({ header: "Podpis", value: () => "", width: "34mm" });
  }

  const colgroup = columns
    .map((c) => `<col${c.width ? ` style="width:${c.width}"` : ""}>`)
    .join("");

  const head = columns
    .map((c) => `<th${c.align ? ` class="${c.align}"` : ""}>${esc(c.header)}</th>`)
    .join("");

  const body = opts.rows
    .map(
      (row, i) =>
        `<tr>${columns
          .map(
            (c) =>
              `<td${c.align ? ` class="${c.align}"` : ""}>${esc(
                c.value(row, i),
              )}</td>`,
          )
          .join("")}</tr>`,
    )
    .join("");

  const footer = (opts.footer ?? [])
    .map((f) => `<div class="foot">${esc(f)}</div>`)
    .join("");

  const generated = new Date().toLocaleString("pl-PL");

  const html = `<!doctype html><html lang="pl"><head><meta charset="utf-8"><title>${esc(
    opts.title,
  )}</title><style>
    @page{size:A4 ${landscape ? "landscape" : "portrait"};margin:12mm}
    body{font-family:Arial,Helvetica,sans-serif;color:#000;margin:0;font-size:10px}
    h1{font-size:15px;margin:0 0 2px}
    .sub{font-size:11px;margin:0 0 10px;color:#333}
    table{width:100%;border-collapse:collapse;table-layout:fixed}
    th,td{border:1px solid #000;padding:3px 5px;vertical-align:top;word-wrap:break-word}
    th{background:#f0f0f0;font-size:9px;text-align:left;font-weight:700}
    thead{display:table-header-group}
    tr{page-break-inside:avoid}
    .center{text-align:center}
    .right{text-align:right}
    .foot{margin-top:8px;font-size:11px;font-weight:700}
    .gen{margin-top:14px;font-size:9px;color:#555}
    @media print{button{display:none}}
  </style></head><body>
    <h1>${esc(opts.title)}</h1>
    ${opts.subtitle ? `<div class="sub">${esc(opts.subtitle)}</div>` : ""}
    <table><colgroup>${colgroup}</colgroup><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
    ${footer}
    <div class="gen">Pozycji: ${opts.rows.length} · wygenerowano ${esc(generated)}</div>
    <script>window.onload=function(){window.print()}</script>
  </body></html>`;

  const w = window.open("", "_blank");
  if (!w) return false;
  w.document.write(html);
  w.document.close();
  return true;
};
